import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChefHat, Plus, Search, LogOut, Clock, Users, Loader2, Edit2, Edit3, Trash2, X, Filter, MoreVertical, Flame, MoveHorizontal, Download, FolderPlus, ExternalLink } from "lucide-react";
import { ChefIcon, WarningIcon, CheckIcon, CelebrationIcon, CameraIcon, MealIcon, FireIcon, VideoIcon, GiftIcon, ClapperboardIcon, StarIcon, CalendarIcon, PersonIcon, DoorIcon, ForkKnifeIcon, ClockIcon as TimeIcon, DownloadIcon, SpoonIcon, NoteIcon, EyeIcon } from '../components/Icons';
import { SmartbouffeLogoSmall } from "../components/Logo";
import { Footer } from "../components/Footer";
import AdBanner from '../components/AdBanner';
import OnboardingTutorial, { useTutorial } from '../components/OnboardingTutorial';
import SubscriptionDialog from '../components/SubscriptionDialog';
import AddRecipeDialog from '../components/AddRecipeDialog';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// PayPal Client ID
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "ATzt5EHARiu7kzylLDyeVIb7Bl36yyS7UexSdpp1gMGOuhOQfkw82b3gv1uvsWAGWJ6OzRCMeY3pZpKi";

// Constante pour les noms des forfaits (utilisée dans le frontend)
const SUBSCRIPTION_PLANS = {
  free: { name: "Gratuit" },
  starter: { name: "Starter" },
  standard: { name: "Standard" },
  pro: { name: "Pro" },
  unlimited: { name: "Illimité" }
};

// Hook pour gérer l'installation PWA
const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installé (mode standalone ou fullscreen)
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches 
      || window.matchMedia('(display-mode: fullscreen)').matches
      || window.navigator.standalone === true; // iOS Safari
    
    if (isInstalled) {
      setCanInstall(false);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Écouter l'événement beforeinstallprompt
    window.addEventListener('beforeinstallprompt', handler);

    // Vérifier si un prompt a été stocké dans le window global (peut arriver si l'événement a été déclenché avant le mount)
    if (window.deferredPWAPrompt) {
      setDeferredPrompt(window.deferredPWAPrompt);
      setCanInstall(true);
    }

    // Écouter les changements de mode d'affichage
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const displayModeHandler = (e) => {
      if (e.matches) {
        setCanInstall(false);
      }
    };
    mediaQuery.addEventListener('change', displayModeHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      mediaQuery.removeEventListener('change', displayModeHandler);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setCanInstall(false);
      setDeferredPrompt(null);
      window.deferredPWAPrompt = null;
      return true;
    }
    return false;
  };

  return { canInstall, install };
};

// Helper pour les badges de régime alimentaire
const getDietBadge = (dietType) => {
  const badges = {
    vegan: { emoji: "🌱", label: "Vegan", color: "#10B981" },
    vegetarian: { emoji: "🥗", label: "Végétarien", color: "#87A96B" },
    pescatarian: { emoji: "🐟", label: "Pescatarien", color: "#3B82F6" },
    carnivore: { emoji: "🥩", label: "Carnivore", color: "#EF4444" },
    mixed: { emoji: "🍽️", label: "Mixte", color: "#8B7355" }
  };
  return badges[dietType] || null;
};

const getStatusBadge = (status) => {
  const badges = {
    'à_faire': { label: 'À faire', icon: <TimeIcon size={16} color="#2196F3" />, color: '#2196F3' },
    'fait': { label: 'Fait', icon: <CheckIcon size={16} color="#4CAF50" />, color: '#4CAF50' },
    'à_refaire': { label: 'À refaire', icon: <StarIcon size={16} color="#FFC107" />, color: '#FFC107' }
  };
  return badges[status];
};

// Liste complète des ingrédients standards
const AVAILABLE_INGREDIENTS = {
  "Viandes & Poissons": [
    "Poulet", "Bœuf haché", "Bacon", "Porc", "Saumon", "Thon", "Crevettes", 
    "Morue", "Dinde", "Jambon", "Agneau", "Côtelettes de porc", 
    "Filet de poisson blanc", "Saucisses", "Steak"
  ],
  "Produits laitiers & Œufs": [
    "Lait", "Œufs", "Crème 15%", "Crème 35%", "Beurre", "Fromage cheddar", 
    "Fromage mozzarella", "Yogourt nature", "Crème sure", "Fromage parmesan", 
    "Fromage à la crème"
  ],
  "Légumes": [
    "Oignon", "Ail", "Tomate", "Poivron", "Carotte", "Brocoli", "Chou-fleur", 
    "Épinards", "Laitue", "Concombre", "Champignons", "Courgette", "Aubergine", 
    "Pomme de terre", "Patate douce", "Céleri", "Maïs", "Pois verts", 
    "Haricots verts", "Chou"
  ],
  "Fruits": [
    "Pomme", "Banane", "Orange", "Citron", "Lime"
  ],
  "Garde-manger": [
    "Pâtes", "Riz", "Farine", "Sucre", "Huile d'olive", "Vinaigre", "Sauce soja", 
    "Moutarde", "Ketchup", "Mayonnaise", "Bouillon de poulet", 
    "Soupe à l'oignon (sachet)", "Tomates en conserve", "Haricots en conserve", 
    "Pâte de tomate"
  ],
  "Épices & Herbes": [
    "Sel", "Poivre", "Paprika", "Cumin", "Origan", "Basilic", "Thym", 
    "Ail en poudre", "Oignon en poudre", "Piment de Cayenne"
  ]
};

const DEFAULT_CATEGORY_IMAGES = [
  // Desserts
  "https://images.unsplash.com/photo-1551024601-bec78aea704b?w=400&h=300&fit=crop", // Gâteaux
  "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop", // Cupcakes
  "https://images.unsplash.com/photo-1486427944299-d1955d23e34d?w=400&h=300&fit=crop", // Pâtisserie
  "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&h=300&fit=crop", // Chocolat
  
  // Plats principaux
  "https://images.unsplash.com/photo-1546548970-71785318a17b?w=400&h=300&fit=crop", // Viandes
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop", // Plats
  "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=300&fit=crop", // Burger
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=300&fit=crop", // Pizza
  
  // Soupes et salades
  "https://images.unsplash.com/photo-1547592166-23ac45744acd?w=400&h=300&fit=crop", // Soupe
  "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop", // Salade
  "https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400&h=300&fit=crop", // Sandwich
  
  // Petit-déjeuner
  "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop", // Pancakes
  "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&h=300&fit=crop", // Café
  "https://images.unsplash.com/photo-1511690656952-34342bb7c2f2?w=400&h=300&fit=crop", // Pain
  
  // Pâtes
  "https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=400&h=300&fit=crop", // Pâtes
  "https://images.unsplash.com/photo-1612874742237-6526221588e3?w=400&h=300&fit=crop", // Spaghetti
  
  // Fruits de mer
  "https://images.unsplash.com/photo-1580822184713-fc5400e7fe10?w=400&h=300&fit=crop", // Sushi
  "https://images.unsplash.com/photo-1559847844-5315695dadae?w=400&h=300&fit=crop", // Fruits de mer
  
  // Végétarien
  "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=300&fit=crop", // Bowl
  "https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?w=400&h=300&fit=crop", // Veggie
  
  // 🍹 Cocktails & Boissons alcoolisées
  "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&h=300&fit=crop", // Cocktail coloré
  "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=400&h=300&fit=crop", // Mojito
  "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop", // Margarita
  "https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=400&h=300&fit=crop", // Cocktails bar
  "https://images.unsplash.com/photo-1587223962930-cb7f31384c19?w=400&h=300&fit=crop", // Whisky cocktail
  
  // 🍷 Sangria & Vin
  "https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=300&fit=crop", // Sangria fruits
  "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop", // Vin rouge
  "https://images.unsplash.com/photo-1474722883778-792e7990302f?w=400&h=300&fit=crop", // Vin blanc
  
  // 🥤 Mocktails & Smoothies
  "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=300&fit=crop", // Smoothie fruits
  "https://images.unsplash.com/photo-1497534446932-c925b458314e?w=400&h=300&fit=crop", // Mocktail coloré
  "https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?w=400&h=300&fit=crop", // Limonade
];

export default function Dashboard() {
  const [categories, setCategories] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showFiltersDialog, setShowFiltersDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [recipeToMove, setRecipeToMove] = useState(null);
  const [newCategoryForMove, setNewCategoryForMove] = useState("");
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [selectedCategoryForRecipe, setSelectedCategoryForRecipe] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [isPreparingExtraction, setIsPreparingExtraction] = useState(false); // État pour feedback visuel immédiat
  const [extractionStep, setExtractionStep] = useState("");
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // États pour vérification durée vidéo
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoCreditCost, setVideoCreditCost] = useState(1);
  const [showDurationConfirmDialog, setShowDurationConfirmDialog] = useState(false);
  
  // PWA Installation
  const { canInstall, install: installPWA } = usePWAInstall();
  
  // States pour extraction depuis photo
  const [extractionMode, setExtractionMode] = useState("video"); // "video", "photo" ou "create"
  const [recipeImageFiles, setRecipeImageFiles] = useState([]); // Max 2 images
  const [recipeImagePreviews, setRecipeImagePreviews] = useState([]);
  const recipeImageInput = useRef(null);
  const [recipeSource, setRecipeSource] = useState("");
  
  // States pour création manuelle de recette
  const [manualRecipe, setManualRecipe] = useState({
    title: "",
    prepTime: "",
    cookTime: "",
    servings: "",
    ingredients: [""],
    instructions: [""]
  });
  const [savingManualRecipe, setSavingManualRecipe] = useState(false);
  
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryImage, setNewCategoryImage] = useState(DEFAULT_CATEGORY_IMAGES[0]);
  const [customImageFile, setCustomImageFile] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // States pour compatibilité alimentaire
  const [showCompatibilityDialog, setShowCompatibilityDialog] = useState(false);
  const [compatibilityData, setCompatibilityData] = useState(null);
  const [extractedRecipe, setExtractedRecipe] = useState(null);
  const [selectedAlternatives, setSelectedAlternatives] = useState({}); // {ingredient: index} pour stocker quel choix (0 ou 1)
  const [showIngredientSearch, setShowIngredientSearch] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [searchingRecipes, setSearchingRecipes] = useState(false);
  const [foundRecipes, setFoundRecipes] = useState([]);
  const [showingSearchResults, setShowingSearchResults] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Long press menu
  const [longPressMenu, setLongPressMenu] = useState({ show: false, recipe: null, x: 0, y: 0 });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [categoryLongPressMenu, setCategoryLongPressMenu] = useState({ show: false, category: null });
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Filtres
  const [prepTimeFilter, setPrepTimeFilter] = useState(null);
  const [ingredientFilter, setIngredientFilter] = useState(null);
  const [meatTypeFilter, setMeatTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null); // "à_faire", "fait", "à_refaire"
  
  // Search with suggestions
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // "Inspire-moi!" feature
  const [showInspireDialog, setShowInspireDialog] = useState(false);
  
  // Système d'abonnement
  const [subscriptionPlan, setSubscriptionPlan] = useState('free');
  const [subscriptionPlanName, setSubscriptionPlanName] = useState('Gratuit');
  const [downloadsLimit, setDownloadsLimit] = useState(3);
  const [downloadsUsed, setDownloadsUsed] = useState(0);
  const [downloadsRemaining, setDownloadsRemaining] = useState(3);
  const [extraDownloads, setExtraDownloads] = useState(0);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [daysUntilRenewal, setDaysUntilRenewal] = useState(30);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // État pour création de catégorie inline (dans le dialog d'ajout)
  const [showInlineNewCategory, setShowInlineNewCategory] = useState(false);
  const [inlineNewCategoryName, setInlineNewCategoryName] = useState("");
  const [creatingInlineCategory, setCreatingInlineCategory] = useState(false);
  
  // Profil utilisateur pour le tutoriel
  const [userProfile, setUserProfile] = useState(null);
  
  // Tutoriel d'onboarding (utilise le profil pour savoir si déjà vu)
  const { showTutorial, completeTutorial } = useTutorial(userProfile);
  
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadCategories();
    loadRecipes();
    loadSubscriptionStatus();
    
    // Gérer le retour de PayPal
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    const extraStatus = urlParams.get('extra');
    // PayPal peut renvoyer subscription_id OU token OU ba_token
    const subscriptionId = urlParams.get('subscription_id') || urlParams.get('token') || urlParams.get('ba_token');
    const token = urlParams.get('token');
    const quantity = urlParams.get('qty');
    
    // Gérer les liens partagés depuis d'autres apps
    const sharedUrl = urlParams.get('share');
    if (sharedUrl) {
      // Pré-remplir le champ URL et ouvrir le dialogue
      setVideoUrl(decodeURIComponent(sharedUrl));
      setShowAddDialog(true);
      toast.success('Lien reçu ! Cliquez sur "Extraire la recette" pour continuer.');
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Vérifier s'il y a une URL en attente (après connexion)
    const pendingUrl = localStorage.getItem('pendingShareUrl');
    if (pendingUrl) {
      setVideoUrl(pendingUrl);
      setShowAddDialog(true);
      toast.success('Lien reçu ! Cliquez sur "Extraire la recette" pour continuer.');
      localStorage.removeItem('pendingShareUrl');
    }
    
    
    if (subscriptionStatus === 'success' && subscriptionId) {
      // Activer l'abonnement après retour PayPal
      axios.post(`${API}/subscription/activate`, { subscription_id: subscriptionId }, { withCredentials: true })
        .then(response => {
          toast.success(`Abonnement ${response.data.plan_name || ''} activé avec succès!`);
          loadSubscriptionStatus();
        })
        .catch(error => {
          toast.error("Erreur lors de l'activation. Veuillez contacter le support.");
        });
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (subscriptionStatus === 'cancelled') {
      toast.info("Abonnement annulé");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (extraStatus === 'success' && token) {
      // Finaliser l'achat de vidéos recettes bonus
      axios.get(`${API}/subscription/complete-extra?token=${token}`, { withCredentials: true })
        .then(response => {
          toast.success(`${response.data.quantity_added || quantity} vidéos recettes ajoutées!`);
          loadSubscriptionStatus();
        })
        .catch(error => {
          toast.error("Erreur lors de la validation. Veuillez contacter le support.");
        });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (extraStatus === 'cancelled') {
      toast.info("Achat annulé");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  const loadSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, { withCredentials: true });
      setSubscriptionPlan(response.data.subscription_plan || 'free');
      setSubscriptionPlanName(response.data.subscription_plan_name || 'Gratuit');
      setDownloadsLimit(response.data.downloads_limit || 3);
      setDownloadsUsed(response.data.downloads_used || 0);
      setDownloadsRemaining(response.data.downloads_remaining || 0);
      setExtraDownloads(response.data.extra_downloads || 0);
      setIsUnlimited(response.data.is_unlimited || false);
      setDaysUntilRenewal(response.data.days_until_renewal || 30);
      setPendingPlanChange(response.data.pending_plan_change);
      // Stocker le profil complet pour le tutoriel
      setUserProfile(response.data);
    } catch (error) {
      toast.error("Erreur de chargement du profil");
    }
  };
  
  const loadSubscriptionPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/subscription/plans`, { withCredentials: true });
      setSubscriptionPlans(response.data.plans || []);
    } catch (error) {
      setSubscriptionPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const openSubscriptionDialog = () => {
    loadSubscriptionPlans();
    setShowSubscriptionDialog(true);
  };

  // Restaurer les résultats de recherche si on revient de RecipeDetail
  useEffect(() => {
    if (location.state?.showSearchResults) {
      setFoundRecipes(location.state.searchResults || []);
      setSelectedIngredients(location.state.selectedIngredients || []);
      setShowingSearchResults(true);
      // Nettoyer le state pour éviter de le réafficher à chaque navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (categories.length > 0) {
      loadRecipes();
    }
  }, [categories.length, selectedCategory?.id, searchQuery, prepTimeFilter, ingredientFilter, meatTypeFilter]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des catégories");
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const params = {};
      if (selectedCategory) {
        params.category_id = selectedCategory.id;
      } else {
        // Si aucune catégorie sélectionnée, limiter aux 10 recettes les plus récentes
        params.limit = 10;
        params.sort = 'recent';
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (prepTimeFilter) {
        params.max_prep_time = prepTimeFilter;
      }
      if (ingredientFilter) {
        if (ingredientFilter === '5-7') {
          params.min_ingredients = 5;
          params.max_ingredients = 7;
        } else if (ingredientFilter === '8-10') {
          params.min_ingredients = 8;
          params.max_ingredients = 10;
        } else if (ingredientFilter === '10+') {
          params.min_ingredients = 10;
        }
      }
      if (meatTypeFilter) {
        params.meat_type = meatTypeFilter;
      }
      
      const response = await axios.get(`${API}/recipes`, { params, withCredentials: true });
      
      setRecipes(response.data);
      setFilteredRecipes(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des recettes");
    }
  };

  // Fonction pour obtenir les suggestions de recherche
  const getSearchSuggestions = (query) => {
    if (!query || query.trim().length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const keyword = query.toLowerCase();
    const allRecipes = recipes;

    const matches = allRecipes.filter(recipe => {
      // Rechercher dans le titre
      if (recipe.title?.toLowerCase().includes(keyword)) return true;
      
      // Rechercher dans les ingrédients
      const allIngredients = [
        ...(recipe.ingredients_metric || []),
        ...(recipe.ingredients_quebec || []),
        ...(recipe.ingredients || [])
      ];
      if (allIngredients.some(ing => ing.toLowerCase().includes(keyword))) return true;
      
      // Rechercher dans les instructions
      const allInstructions = [
        ...(recipe.instructions || []),
        ...(recipe.original_instructions || [])
      ];
      if (allInstructions.some(inst => inst.toLowerCase().includes(keyword))) return true;
      
      return false;
    });

    // Limiter à 5 suggestions
    setSearchSuggestions(matches.slice(0, 5));
    setShowSuggestions(matches.length > 0);
  };

  // Fonction pour effectuer la recherche complète
  const handleSearch = () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error("Veuillez entrer au moins 2 caractères");
      return;
    }

    const keyword = searchQuery.toLowerCase();
    const allRecipes = recipes;

    const results = allRecipes.filter(recipe => {
      // Rechercher dans le titre
      if (recipe.title?.toLowerCase().includes(keyword)) return true;
      
      // Rechercher dans les ingrédients
      const allIngredients = [
        ...(recipe.ingredients_metric || []),
        ...(recipe.ingredients_quebec || []),
        ...(recipe.ingredients || [])
      ];
      if (allIngredients.some(ing => ing.toLowerCase().includes(keyword))) return true;
      
      // Rechercher dans les instructions
      const allInstructions = [
        ...(recipe.instructions || []),
        ...(recipe.original_instructions || [])
      ];
      if (allInstructions.some(inst => inst.toLowerCase().includes(keyword))) return true;
      
      return false;
    });

    setSearchResults(results);
    setIsSearchActive(true);
    setShowSuggestions(false);
    setSelectedCategory(null); // Désélectionner la catégorie
    setShowingSearchResults(false); // Masquer les résultats de "Qu'est-ce qu'on mange?"
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("L'image ne doit pas dépasser 5 Mo");
        return;
      }
      
      // Vérifier le type
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez sélectionner une image");
        return;
      }
      
      setCustomImageFile(file);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomImagePreview(reader.result);
        setNewCategoryImage(reader.result); // Utiliser l'image uploadée
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Veuillez entrer un nom de catégorie");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: newCategoryName, image_url: newCategoryImage },
        { withCredentials: true }
      );
      setCategories([...categories, response.data]);
      setShowCategoryDialog(false);
      setNewCategoryName("");
      setNewCategoryImage(DEFAULT_CATEGORY_IMAGES[0]);
      setCustomImageFile(null);
      setCustomImagePreview(null);
      toast.success("Catégorie créée!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleUpdateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Veuillez entrer un nom de catégorie");
      return;
    }

    try {
      const response = await axios.put(
        `${API}/categories/${editingCategory.id}`,
        { name: newCategoryName, image_url: newCategoryImage },
        { withCredentials: true }
      );
      setCategories(categories.map(c => c.id === editingCategory.id ? response.data : c));
      setEditingCategory(null);
      setShowCategoryDialog(false);
      setNewCategoryName("");
      setNewCategoryImage(DEFAULT_CATEGORY_IMAGES[0]);
      setCustomImageFile(null);
      setCustomImagePreview(null);
      toast.success("Catégorie modifiée!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  const confirmDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeleteCategoryDialog(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await axios.delete(`${API}/categories/${categoryToDelete.id}`, { withCredentials: true });
      setCategories(categories.filter(c => c.id !== categoryToDelete.id));
      if (selectedCategory?.id === categoryToDelete.id) {
        setSelectedCategory(null);
      }
      
      // Afficher un message avec le nombre de recettes supprimées
      const recipesDeleted = response.data.recipes_deleted || 0;
      if (recipesDeleted > 0) {
        toast.success(`Catégorie supprimée avec ${recipesDeleted} recette${recipesDeleted > 1 ? 's' : ''}`);
      } else {
        toast.success("Catégorie supprimée");
      }
      
      setShowDeleteCategoryDialog(false);
      setCategoryToDelete(null);
      
      // Recharger les recettes pour mettre à jour la vue
      await loadRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  // Créer une catégorie depuis le dialogue d'ajout de recette (inline)
  const handleCreateInlineCategory = async () => {
    if (!inlineNewCategoryName.trim()) {
      toast.error("Veuillez entrer un nom de catégorie");
      return;
    }

    setCreatingInlineCategory(true);
    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: inlineNewCategoryName, image_url: DEFAULT_CATEGORY_IMAGES[0] },
        { withCredentials: true }
      );
      
      // Ajouter la nouvelle catégorie et la sélectionner
      setCategories(prev => [...prev, response.data]);
      setSelectedCategoryForRecipe(response.data.id);
      
      // Réinitialiser le formulaire inline
      setShowInlineNewCategory(false);
      setInlineNewCategoryName("");
      
      toast.success(`Catégorie "${response.data.name}" créée!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreatingInlineCategory(false);
    }
  };

  const handleImageUploadForRecipe = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Vérifier le nombre (max 2)
    const currentCount = recipeImageFiles.length;
    const newFiles = files.slice(0, 2 - currentCount);
    
    if (currentCount + newFiles.length > 2) {
      toast.error("Maximum 2 images");
      return;
    }
    
    // Vérifier chaque fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];
    for (const file of newFiles) {
      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Chaque image ne doit pas dépasser 10 MB");
        return;
      }
      
      // Vérifier le type
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format non supporté. Utilisez JPG, PNG, HEIC ou PDF");
        return;
      }
    }
    
    // Ajouter les nouveaux fichiers
    const updatedFiles = [...recipeImageFiles, ...newFiles];
    setRecipeImageFiles(updatedFiles);
    
    // Créer les aperçus
    const newPreviews = [...recipeImagePreviews];
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        setRecipeImagePreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const updatedFiles = recipeImageFiles.filter((_, i) => i !== index);
    const updatedPreviews = recipeImagePreviews.filter((_, i) => i !== index);
    setRecipeImageFiles(updatedFiles);
    setRecipeImagePreviews(updatedPreviews);
  };

  // Gestion du long press pour afficher le menu d'options
  const handleLongPressStart = (e, recipe) => {
    e.preventDefault();
    
    // Nettoyer le timer précédent s'il existe
    if (longPressTimer) {
      clearTimeout(longPressTimer);
    }
    
    // Démarrer un nouveau timer (500ms pour déclencher le long press)
    const timer = setTimeout(() => {
      // Vibration sur mobile si disponible
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Afficher le menu
      setLongPressMenu({
        show: true,
        recipe: recipe,
        x: e.clientX || (e.touches && e.touches[0].clientX) || 0,
        y: e.clientY || (e.touches && e.touches[0].clientY) || 0
      });
    }, 500);
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    // Annuler le timer si l'utilisateur relâche avant 500ms
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const closeLongPressMenu = () => {
    setLongPressMenu({ show: false, recipe: null, x: 0, y: 0 });
  };

  // Gestion du long press pour les catégories
  const handleCategoryLongPressStart = (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (longPressTimer) {
      clearTimeout(longPressTimer);
    }
    
    const timer = setTimeout(() => {
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      setCategoryLongPressMenu({
        show: true,
        category: category
      });
    }, 500);
    
    setLongPressTimer(timer);
  };

  const closeCategoryLongPressMenu = () => {
    setCategoryLongPressMenu({ show: false, category: null });
  };

  // Fonction pour vérifier la durée de la vidéo avant extraction
  const checkVideoDurationAndExtract = async () => {
    if (!videoUrl.trim()) {
      toast.error("Veuillez entrer une URL vidéo");
      return;
    }

    if (!selectedCategoryForRecipe) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    // Feedback visuel immédiat - désactive le bouton et montre l'état de préparation
    setIsPreparingExtraction(true);

    try {
      // Vérifier la durée
      const response = await axios.post(
        `${API}/recipes/check-duration`,
        { video_url: videoUrl, category_id: selectedCategoryForRecipe },
        { withCredentials: true }
      );

      const { allowed, credit_cost, requires_confirmation, message, duration_minutes } = response.data;

      if (!allowed) {
        toast.error(message);
        return;
      }

      setVideoCreditCost(credit_cost);
      setVideoDuration(duration_minutes);

      // Si nécessite confirmation (vidéo 3:30-5:00), afficher dialog
      if (requires_confirmation) {
        setShowDurationConfirmDialog(true);
        setIsPreparingExtraction(false); // On attend la confirmation de l'utilisateur
      } else {
        // Sinon, extraire directement
        await handleExtractRecipe(credit_cost);
      }
    } catch (error) {
      setIsPreparingExtraction(false); // Réinitialiser en cas d'erreur
      toast.error(error.response?.data?.detail || "Impossible de vérifier la durée de cette vidéo");
    }
  };

  const handleExtractFromImage = async () => {
    if (recipeImageFiles.length === 0) {
      toast.error("Veuillez sélectionner au moins une image");
      return;
    }

    if (!selectedCategoryForRecipe) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    // Feedback visuel immédiat
    setIsPreparingExtraction(true);
    setExtracting(true);
    setExtractionStep("Téléchargement des photos...");
    
    // Progression pour l'image
    const imageSteps = [
      "Téléchargement des photos...",
      "Analyse des images...",
      "Extraction de la recette...",
      "Finalisation..."
    ];
    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      stepIndex++;
      if (stepIndex < imageSteps.length) {
        setExtractionStep(imageSteps[stepIndex]);
      }
    }, 5000);
    
    try {
      const formData = new FormData();
      // Ajouter toutes les images
      recipeImageFiles.forEach((file) => {
        formData.append('images', file);
      });
      formData.append('category_id', selectedCategoryForRecipe);
      if (recipeSource.trim()) {
        formData.append('source', recipeSource);
      }
      
      const response = await axios.post(
        `${API}/recipes/extract-from-image`,
        formData,
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      clearInterval(progressInterval);
      
      
      // Vérifier si il y a des incompatibilités
      const compatibility = response.data.compatibility;
      
      if (compatibility && !compatibility.compatible) {
        const hasAllergies = compatibility.has_allergies;
        const hasDietIssues = compatibility.diet_issues && compatibility.diet_issues.length > 0;
        
        if (hasAllergies && !hasDietIssues) {
          toast.warning(
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <WarningIcon size={18} />
              <span>Cette recette contient des allergènes: {compatibility.allergens.join(', ')}</span>
            </div>, 
            { duration: 6000 }
          );
          setShowAddDialog(false);
          setRecipeImageFiles([]);
          setRecipeImagePreviews([]);
          setRecipeSource("");
          setSelectedCategoryForRecipe("");
          // Revenir à "Toutes les recettes" au lieu de la catégorie choisie
          setSelectedCategory(null);
          await loadRecipes();
        } else if (hasDietIssues) {
          setExtractedRecipe(response.data);
          setCompatibilityData(compatibility);
          setShowCompatibilityDialog(true);
          setShowAddDialog(false);
          
          if (hasAllergies) {
            toast.warning(`⚠️ Attention: cette recette contient aussi des allergènes: ${compatibility.allergens.join(', ')}`, { duration: 6000 });
          }
        }
      } else {
        setShowAddDialog(false);
        setRecipeImageFiles([]);
        setRecipeImagePreviews([]);
        setRecipeSource("");
        setSelectedCategoryForRecipe("");
        toast.success("Recette extraite avec succès!");
        // Revenir à "Toutes les recettes" au lieu de la catégorie choisie
        setSelectedCategory(null);
        await loadRecipes();
        await loadSubscriptionStatus(); // Recharger le statut après extraction
      }
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'extraction";
      
      // Si c'est une erreur de crédits insuffisants, ouvrir le dialog d'achat
      if (errorMessage.includes("Crédits insuffisants") || errorMessage.includes("crédits")) {
        toast.error(errorMessage);
        setShowCreditsDialog(true);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setExtracting(false);
      setExtractionStep("");
      setExtractionProgress(0);
      setIsPreparingExtraction(false); // Toujours réinitialiser l'état de préparation
    }
  };

  const handleExtractRecipe = async (creditCost = 1) => {
    if (!videoUrl.trim()) {
      toast.error("Veuillez entrer une URL vidéo");
      setIsPreparingExtraction(false);
      return;
    }

    if (!selectedCategoryForRecipe) {
      toast.error("Veuillez sélectionner une catégorie");
      setIsPreparingExtraction(false);
      return;
    }

    // Activer l'état d'extraction (le modal de progression s'affiche)
    setExtracting(true);
    setIsPreparingExtraction(false); // On peut désactiver le gris car le modal apparaît
    setExtractionStep("Lancement de l'extraction...");
    setExtractionProgress(0);
    setShowDurationConfirmDialog(false); // Fermer le dialog de confirmation
    
    try {
      // Lancer l'extraction (retourne immédiatement avec un task_id)
      const response = await axios.post(
        `${API}/recipes/extract`,
        { 
          video_url: videoUrl, 
          category_id: selectedCategoryForRecipe,
          credit_cost: creditCost
        },
        { withCredentials: true }
      );
      
      // Si l'extraction est asynchrone, faire du polling
      if (response.data.async && response.data.task_id) {
        const taskId = response.data.task_id;
        toast.info("Extraction en cours... Cela peut prendre jusqu'à 2 minutes.", { duration: 5000 });
        
        // Polling pour vérifier le statut
        let completed = false;
        let attempts = 0;
        const maxAttempts = 60; // 60 x 3 secondes = 3 minutes max
        
        while (!completed && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 3000)); // Attendre 3 secondes
          attempts++;
          
          try {
            const statusResponse = await axios.get(
              `${API}/recipes/extraction-status/${taskId}`,
              { withCredentials: true }
            );
            
            const status = statusResponse.data;
            
            // Mettre à jour la progression
            setExtractionStep(status.message || `Extraction en cours...`);
            if (typeof status.progress === 'number' && status.progress >= 0) {
              setExtractionProgress(prev => Math.max(prev, status.progress));
            }
            
            if (status.status === 'completed') {
              completed = true;
              
              // Vérifier si il y a des incompatibilités
              const recipe = status.recipe;
              const compatibility = recipe?.compatibility;
              
              if (compatibility && !compatibility.compatible) {
                const hasAllergies = compatibility.has_allergies;
                const hasDietIssues = compatibility.diet_issues && compatibility.diet_issues.length > 0;
                
                if (hasAllergies && !hasDietIssues) {
                  toast.warning(
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <WarningIcon size={18} />
                      <span>Cette recette contient des allergènes: {compatibility.allergens.join(', ')}</span>
                    </div>, 
                    { duration: 6000 }
                  );
                  setShowAddDialog(false);
                  setVideoUrl("");
                  setSelectedCategoryForRecipe("");
                  setSelectedCategory(null);
                  await loadRecipes();
                } else if (hasDietIssues) {
                  setExtractedRecipe(recipe);
                  setCompatibilityData(compatibility);
                  setShowCompatibilityDialog(true);
                  setShowAddDialog(false);
                  
                  if (hasAllergies) {
                    toast.warning(`⚠️ Attention: cette recette contient aussi des allergènes: ${compatibility.allergens.join(', ')}`, { duration: 6000 });
                  }
                }
              } else {
                setShowAddDialog(false);
                setVideoUrl("");
                setSelectedCategoryForRecipe("");
                toast.success("Recette extraite avec succès!");
                setSelectedCategory(null);
                await loadRecipes();
                await loadSubscriptionStatus();
              }
            } else if (status.status === 'failed') {
              completed = true;
              const errorMessage = status.message || status.error || "Erreur lors de l'extraction";
              toast.error(errorMessage);
            }
            // Sinon continuer le polling (status = pending ou processing)
            
          } catch (pollError) {
            // Erreur réseau temporaire, continuer le polling
            await new Promise(r => setTimeout(r, 2000));
          }
        }
        
        if (!completed) {
          toast.error("L'extraction prend trop de temps. Veuillez réessayer plus tard.");
        }
        
      } else {
        // Ancienne réponse synchrone (pour compatibilité)
        
        const compatibility = response.data.compatibility;
        
        if (compatibility && !compatibility.compatible) {
          const hasAllergies = compatibility.has_allergies;
          const hasDietIssues = compatibility.diet_issues && compatibility.diet_issues.length > 0;
          
          if (hasAllergies && !hasDietIssues) {
            toast.warning(
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <WarningIcon size={18} />
                <span>Cette recette contient des allergènes: {compatibility.allergens.join(', ')}</span>
              </div>, 
              { duration: 6000 }
            );
            setShowAddDialog(false);
            setVideoUrl("");
            setSelectedCategoryForRecipe("");
            setSelectedCategory(null);
            await loadRecipes();
          } else if (hasDietIssues) {
            setExtractedRecipe(response.data);
            setCompatibilityData(compatibility);
            setShowCompatibilityDialog(true);
            setShowAddDialog(false);
            
            if (hasAllergies) {
              toast.warning(`⚠️ Attention: cette recette contient aussi des allergènes: ${compatibility.allergens.join(', ')}`, { duration: 6000 });
            }
          }
        } else {
          setShowAddDialog(false);
          setVideoUrl("");
          setSelectedCategoryForRecipe("");
          toast.success("Recette extraite avec succès!");
          setSelectedCategory(null);
          await loadRecipes();
          await loadSubscriptionStatus();
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'extraction";
      
      if (errorMessage.includes("Crédits insuffisants") || errorMessage.includes("crédits") || errorMessage.includes("Téléchargements insuffisants")) {
        toast.error(errorMessage);
        setShowCreditsDialog(true);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setExtracting(false);
      setExtractionStep("");
      setExtractionProgress(0);
    }
  };

  const handleMoveRecipe = async () => {
    if (!newCategoryForMove) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    try {
      await axios.put(
        `${API}/recipes/${recipeToMove.id}`,
        { category_id: newCategoryForMove },
        { withCredentials: true }
      );
      
      toast.success("Recette déplacée avec succès");
      setShowMoveDialog(false);
      setRecipeToMove(null);
      setNewCategoryForMove("");
      
      // Recharger les recettes
      await loadRecipes();
    } catch (error) {
      toast.error("Erreur lors du déplacement de la recette");
    }
  };

  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) return;
    
    try {
      await axios.delete(
        `${API}/recipes/${recipeToDelete.id}`,
        { withCredentials: true }
      );
      
      toast.success("Recette supprimée avec succès!");
      setShowDeleteDialog(false);
      setRecipeToDelete(null);
      await loadRecipes();
    } catch (error) {
      toast.error("Erreur lors de la suppression de la recette");
    }
  };

  const handleGetRandomRecipe = async (status = null) => {
    try {
      const statusParam = status && status !== 'tout' ? `?status=${status}` : '';
      const response = await axios.get(
        `${API}/recipes/random${statusParam}`,
        { withCredentials: true }
      );
      
      // Naviguer vers la recette
      navigate(`/recipe/${response.data.id}`, {
        state: { fromInspire: true, inspireStatus: status }
      });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Aucune recette trouvée avec ce critère");
      } else {
        toast.error("Erreur lors de la récupération de la recette");
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate("/");
      toast.success("Déconnexion réussie");
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleAcceptAlternatives = async () => {
    try {
      const alternatives = compatibilityData.alternatives || {};
      
      // Créer un objet avec les alternatives sélectionnées par l'utilisateur
      const selectedAlts = {};
      Object.keys(alternatives).forEach(ingredient => {
        const altArray = alternatives[ingredient];
        if (Array.isArray(altArray)) {
          // Utiliser l'alternative sélectionnée (0 ou 1), par défaut 0
          const selectedIndex = selectedAlternatives[ingredient] || 0;
          selectedAlts[ingredient] = altArray[selectedIndex];
        } else {
          // Ancien format (single alternative)
          selectedAlts[ingredient] = altArray;
        }
      });
      
      // Utiliser le nouvel endpoint backend qui réécrit intelligemment les instructions
      await axios.post(
        `${API}/recipes/${extractedRecipe.id}/apply-alternatives`,
        selectedAlts,
        { withCredentials: true }
      );
      
      setShowCompatibilityDialog(false);
      setVideoUrl("");
      setSelectedCategoryForRecipe("");
      setSelectedAlternatives({}); // Reset les sélections
      toast.success("Recette modifiée avec les alternatives!");
      
      const category = categories.find(c => c.id === extractedRecipe.category_id);
      setSelectedCategory(category);
      await loadRecipes();
    } catch (error) {
      toast.error("Erreur lors de la modification de la recette");
    }
  };

  const handleKeepOriginal = async () => {
    setShowCompatibilityDialog(false);
    setVideoUrl("");
    setSelectedCategoryForRecipe("");
    toast.success("Recette extraite telle quelle!");
    const category = categories.find(c => c.id === extractedRecipe.category_id);
    setSelectedCategory(category);
    await loadRecipes();
  };

  // Fonction pour sauvegarder une recette créée manuellement
  const handleSaveManualRecipe = async () => {
    // Validation
    if (!manualRecipe.title.trim()) {
      toast.error("Veuillez entrer un titre pour la recette");
      return;
    }
    if (!selectedCategoryForRecipe) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }
    const validIngredients = manualRecipe.ingredients.filter(i => i.trim());
    if (validIngredients.length === 0) {
      toast.error("Veuillez ajouter au moins un ingrédient");
      return;
    }
    const validInstructions = manualRecipe.instructions.filter(i => i.trim());
    if (validInstructions.length === 0) {
      toast.error("Veuillez ajouter au moins une instruction");
      return;
    }

    setSavingManualRecipe(true);
    try {
      const response = await axios.post(
        `${API}/recipes/create-manual`,
        {
          title: manualRecipe.title.trim(),
          category_id: selectedCategoryForRecipe,
          prep_time: manualRecipe.prepTime.trim() || null,
          cook_time: manualRecipe.cookTime.trim() || null,
          servings: manualRecipe.servings.trim() || null,
          ingredients: validIngredients,
          instructions: validInstructions
        },
        { withCredentials: true }
      );

      toast.success("Recette créée avec succès!");
      
      // Sauvegarder la catégorie AVANT de réinitialiser
      const savedCategoryId = selectedCategoryForRecipe;
      const category = categories.find(c => c.id === savedCategoryId);
      
      // Réinitialiser le formulaire
      setManualRecipe({
        title: "",
        prepTime: "",
        cookTime: "",
        servings: "",
        ingredients: [""],
        instructions: [""]
      });
      setSelectedCategoryForRecipe("");
      setShowAddDialog(false);
      
      // Afficher la catégorie et charger les recettes
      if (category) {
        setSelectedCategory(category);
      }
      await loadRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la recette");
    } finally {
      setSavingManualRecipe(false);
    }
  };

  // Fonctions helper pour le formulaire manuel
  const addIngredientField = () => {
    setManualRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, ""]
    }));
  };

  const removeIngredientField = (index) => {
    if (manualRecipe.ingredients.length > 1) {
      setManualRecipe(prev => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  const updateIngredient = (index, value) => {
    setManualRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === index ? value : ing)
    }));
  };

  const addInstructionField = () => {
    setManualRecipe(prev => ({
      ...prev,
      instructions: [...prev.instructions, ""]
    }));
  };

  const removeInstructionField = (index) => {
    if (manualRecipe.instructions.length > 1) {
      setManualRecipe(prev => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const updateInstruction = (index, value) => {
    setManualRecipe(prev => ({
      ...prev,
      instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
    }));
  };


  const toggleIngredient = (ingredient) => {
    setSelectedIngredients(prev =>
      prev.includes(ingredient)
        ? prev.filter(i => i !== ingredient)
        : [...prev, ingredient]
    );
  };

  const handleSearchByIngredients = async () => {
    if (selectedIngredients.length === 0) {
      toast.error("Sélectionnez au moins un ingrédient");
      return;
    }

    setSearchingRecipes(true);
    try {
      const response = await axios.post(
        `${API}/recipes/search-by-ingredients`,
        selectedIngredients,
        { withCredentials: true }
      );

      setFoundRecipes(response.data);
      
      if (response.data.length === 0) {
        toast.info("Aucune recette trouvée avec ces ingrédients (minimum 5 correspondances requises)");
      } else {
        // Fermer le dialog et afficher les résultats sur le dashboard
        setShowIngredientSearch(false);
        setShowingSearchResults(true);
        setSelectedCategory(null); // Désélectionner toute catégorie
        toast.success(`${response.data.length} recette(s) trouvée(s) !`);
      }
    } catch (error) {
      toast.error("Erreur lors de la recherche");
    } finally {
      setSearchingRecipes(false);
    }
  };

  const handleBackToAllRecipes = () => {
    setShowingSearchResults(false);
    setFoundRecipes([]);
    setSelectedIngredients([]);
    loadRecipes();
  };

  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setNewCategoryName(category.name);
      setNewCategoryImage(category.image_url);
      setCustomImageFile(null);
      setCustomImagePreview(null);
    } else {
      setEditingCategory(null);
      setNewCategoryName("");
      setNewCategoryImage(DEFAULT_CATEGORY_IMAGES[0]);
      setCustomImageFile(null);
      setCustomImagePreview(null);
    }
    setShowCategoryDialog(true);
  };

  if (loading) {
    return (
      <div className="loading-screen" data-testid="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* Tutoriel d'onboarding pour nouveaux utilisateurs */}
      {showTutorial && (
        <OnboardingTutorial onComplete={completeTutorial} />
      )}
      
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left" style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative'}}>
            <div 
              style={{
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                transition: 'background 0.2s'
              }} 
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(135, 169, 107, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <SmartbouffeLogoSmall className="header-logo-icon" />
              <MoreVertical size={18} color="#5D6F61" style={{ marginLeft: '2px' }} />
            </div>
            {showProfileMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '0.5rem',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '0.5rem',
                zIndex: 1000,
                minWidth: '180px'
              }}>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/menu');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    color: '#5D6F61',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#F5F1E8'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <CalendarIcon size={18} color="#5D6F61" /> Menu
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/profile');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    color: '#5D6F61',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#F5F1E8'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <PersonIcon size={18} color="#5D6F61" /> Profil
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    handleLogout();
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    color: '#5D6F61',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#F5F1E8'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <DoorIcon size={18} color="#5D6F61" /> Déconnexion
                </button>
              </div>
            )}
            <h1>smartbouffe</h1>
          </div>
          <div className="header-right">
            <div 
              onClick={openSubscriptionDialog}
              data-testid="subscription-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: isUnlimited 
                  ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' 
                  : 'linear-gradient(135deg, #87A96B 0%, #9CAF88 100%)',
                borderRadius: '20px',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 2px 8px rgba(135, 169, 107, 0.3)',
                marginRight: '1rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(135, 169, 107, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(135, 169, 107, 0.3)';
              }}
            >
              <DownloadIcon size={18} color="white" />
              <span style={{ 
                fontWeight: '600', 
                color: 'white',
                fontSize: '0.95rem'
              }}>
                {isUnlimited ? '∞ Illimité' : `${downloadsRemaining + extraDownloads}/${downloadsLimit}`}
              </span>
            </div>
            
            {/* Bouton installer PWA */}
            {canInstall && (
              <div
                onClick={async () => {
                  const installed = await installPWA();
                  if (installed) {
                    toast.success("🎉 SmartBouffe installé sur votre appareil!");
                  }
                }}
                data-testid="install-pwa-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
                  marginRight: '1rem',
                  animation: 'pulse 2s infinite'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Download size={18} color="white" />
                <span style={{ 
                  fontWeight: '600', 
                  color: 'white',
                  fontSize: '0.85rem'
                }}>
                  Installer
                </span>
              </div>
            )}
            
            <span className="user-name" data-testid="user-name">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {/* Categories Carousel */}
        <div className="categories-section">
          <h2 className="section-title">Catégories</h2>
          <div className="categories-carousel">
            <div className="categories-scroll">
              {categories.map(category => (
                <div 
                  key={category.id} 
                  className={`category-card ${selectedCategory?.id === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(selectedCategory?.id === category.id ? null : category)}
                  onMouseDown={(e) => handleCategoryLongPressStart(e, category)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={(e) => handleCategoryLongPressStart(e, category)}
                  onTouchEnd={handleLongPressEnd}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    handleCategoryLongPressStart(e, category);
                  }}
                  data-testid={`category-${category.id}`}
                >
                  <div className="category-image-wrapper">
                    <img src={category.image_url} alt={category.name} className="category-image" />
                  </div>
                  <h3 className="category-name">{category.name}</h3>
                </div>
              ))}
              
              {/* Petit bouton + pour ajouter une catégorie */}
              <div 
                onClick={() => openCategoryDialog()}
                data-testid="add-category-btn"
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #9CAF88 0%, #87A96B 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(135, 169, 107, 0.3)',
                  flexShrink: 0,
                  alignSelf: 'center',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(135, 169, 107, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(135, 169, 107, 0.3)';
                }}
              >
                <Plus size={24} color="white" />
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-controls">
          <div className="search-bar" style={{ position: 'relative', flex: 1, maxWidth: '500px' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search className="search-icon" />
                <Input
                  placeholder="Rechercher une recette..."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    getSearchSuggestions(value);
                    // Reset search if field is cleared
                    if (!value.trim()) {
                      setIsSearchActive(false);
                      setSearchResults([]);
                      setShowSuggestions(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onFocus={() => {
                    if (searchQuery.trim().length >= 2) {
                      getSearchSuggestions(searchQuery);
                    }
                  }}
                  className="search-input"
                  data-testid="search-input"
                  style={{ paddingRight: '40px' }}
                />
                
                {/* Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '2px solid #D4A574',
                    borderRadius: '8px',
                    marginTop: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {searchSuggestions.map((recipe) => (
                      <div
                        key={recipe.id}
                        onClick={() => {
                          setShowSuggestions(false);
                          navigate(`/recipe/${recipe.id}`);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #F5F1E8',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F5F1E8'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <div style={{ fontWeight: '500', color: '#8B7355', marginBottom: '4px' }}>
                          {recipe.title}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#A0A0A0' }}>
                          {recipe.prep_time && `⏱️ ${recipe.prep_time} min`}
                          {recipe.ingredients_quebec && ` • 🥘 ${recipe.ingredients_quebec.length} ingrédients`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button
                onClick={handleSearch}
                style={{
                  background: '#87A96B',
                  color: 'white',
                  whiteSpace: 'nowrap'
                }}
                disabled={!searchQuery || searchQuery.trim().length < 2}
              >
                Rechercher
              </Button>
            </div>
          </div>
          
          {!selectedCategory && (
            <>
              <Button 
                onClick={() => setShowIngredientSearch(true)} 
                style={{
                  background: '#87A96B',
                  color: 'white',
                  marginRight: '0.75rem'
                }}
              >
                <MealIcon size={20} color="#FFFFFF" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Qu'est-ce qu'on mange?
              </Button>
              <Button 
                onClick={() => setShowInspireDialog(true)}
                data-testid="inspire-btn"
                style={{
                  background: '#87A96B',
                  color: 'white',
                  marginRight: '0.75rem'
                }}
              >
                <CelebrationIcon size={18} color="#FFFFFF" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Inspire-moi!
              </Button>
              <Button 
                onClick={() => navigate('/discover')}
                data-testid="discover-btn"
                style={{
                  background: 'linear-gradient(135deg, #D4A574, #C4956A)',
                  color: 'white',
                  marginRight: '0.75rem',
                  fontWeight: 700
                }}
              >
                <StarIcon size={18} color="#FFFFFF" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Découvrir
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            onClick={() => setShowFiltersDialog(true)}
            data-testid="filters-btn"
          >
            <Filter size={18} className="mr-2" />
            Filtres
            {(prepTimeFilter || ingredientFilter || meatTypeFilter) && (
              <span className="filter-badge">•</span>
            )}
          </Button>
        </div>

        {/* Indicateur de catégorie sélectionnée */}
        {selectedCategory && (
          <div style={{
            padding: '1rem',
            background: '#F5F1E8',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '2px solid #D4A574'
          }}>
            <div>
              <h3 style={{ margin: 0, color: '#8B7355', fontSize: '1.1rem' }}>
                📁 {selectedCategory.name}
              </h3>
              <p style={{ margin: '0.25rem 0 0 0', color: '#8B7355', fontSize: '0.9rem' }}>
                {filteredRecipes.length} recette{filteredRecipes.length > 1 ? 's' : ''}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedCategory(null)}
              style={{
                borderColor: '#D4A574',
                color: '#8B7355'
              }}
            >
              Voir toutes les recettes
            </Button>
          </div>
        )}

        {categories.length === 0 ? (
          <div className="empty-state" data-testid="empty-categories">
            <ChefHat size={64} className="empty-icon" />
            <h2>Aucune catégorie</h2>
            <p>Créez votre première catégorie pour organiser vos recettes</p>
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <ChefHat size={64} className="empty-icon" />
            <h2>Aucune recette</h2>
            <p>Cliquez sur le bouton + pour ajouter votre première recette</p>
          </div>
        ) : (
          <>
            {/* Bannière de résultats de recherche */}
            {showingSearchResults && (
              <div style={{
                background: '#E8F4DB',
                border: '3px solid #87A96B',
                borderRadius: '12px',
                padding: '1.25rem 1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <h3 style={{ color: '#3A4A3E', margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                    <CelebrationIcon size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                    {foundRecipes.length} recette(s) trouvée(s) avec vos ingrédients
                  </h3>
                  <p style={{ color: '#5D6F61', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                    {selectedIngredients.length} ingrédient(s) : {selectedIngredients.join(', ')}
                  </p>
                </div>
                <Button
                  onClick={handleBackToAllRecipes}
                  style={{ 
                    background: '#DC3545',
                    color: 'white',
                    fontWeight: '600',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
                  }}
                >
                  ✕ Quitter les suggestions
                </Button>
              </div>
            )}

            {/* Bannière des résultats de recherche par mot-clé */}
            {isSearchActive && (
              <div style={{
                background: '#FFF9E6',
                border: '3px solid #D4A574',
                borderRadius: '12px',
                padding: '1.25rem 1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <h3 style={{ color: '#8B7355', margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                    🔍 {searchResults.length} recette(s) trouvée(s)
                  </h3>
                  <p style={{ color: '#A0826D', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                    Recherche : "{searchQuery}"
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSearchActive(false);
                    setSearchResults([]);
                    setSearchQuery('');
                  }}
                  style={{
                    borderColor: '#D4A574',
                    color: '#8B7355',
                    background: 'white'
                  }}
                >
                  ✕ Quitter la recherche
                </Button>
              </div>
            )}

            <div className={selectedCategory ? "recipes-grid category-view" : "recipes-grid"}>
              {(() => {
                // Déterminer quelles recettes afficher
                let displayRecipes = filteredRecipes;
                
                if (isSearchActive) {
                  // Afficher les résultats de la recherche par mot-clé
                  displayRecipes = searchResults;
                } else if (showingSearchResults) {
                  // Afficher les résultats de "Qu'est-ce qu'on mange?"
                  displayRecipes = foundRecipes;
                }
                
                // Appliquer le filtre de statut
                if (statusFilter) {
                  displayRecipes = displayRecipes.filter(recipe => recipe.status === statusFilter);
                }
                
                return (isSearchActive && searchResults.length === 0) ? (
                  // Message si aucun résultat de recherche par mot-clé
                  <div className="empty-state">
                    <Search size={64} className="empty-icon" />
                    <h2>Aucune recette trouvée</h2>
                    <p>Essayez avec d'autres mots-clés</p>
                  </div>
                ) : showingSearchResults ? (
                // Afficher les résultats de recherche
                displayRecipes.length === 0 ? (
                  <div className="empty-state">
                    <p>Aucune recette trouvée avec ces ingrédients</p>
                  </div>
                ) : (
                  displayRecipes.map((recipe) => {
                    const category = categories.find(c => c.id === recipe.category_id);
                    return (
                      <div key={recipe.id} className="recipe-card" style={{ position: 'relative' }}>
                        {/* Menu 3 points en haut à gauche */}
                        <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 10 }}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  background: 'rgba(245, 241, 232, 0.95)',
                                  border: '1px solid #D4A574',
                                  cursor: 'pointer',
                                  padding: '6px',
                                  color: '#8B7355',
                                  display: 'flex',
                                  alignItems: 'center',
                                  borderRadius: '6px',
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#FFF9E6';
                                  e.currentTarget.style.borderColor = '#87A96B';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(245, 241, 232, 0.95)';
                                  e.currentTarget.style.borderColor = '#D4A574';
                                }}
                                title="Options"
                              >
                                <MoreVertical size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRecipeToMove(recipe);
                                  setNewCategoryForMove(recipe.category_id);
                                  setShowMoveDialog(true);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <MoveHorizontal size={16} style={{ marginRight: '0.5rem' }} />
                                Déplacer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRecipeToDelete(recipe);
                                  setShowDeleteDialog(true);
                                }}
                                style={{ cursor: 'pointer', color: '#DC3545' }}
                              >
                                <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="recipe-card-header">
                          <h3 onClick={() => {
                            navigate(`/recipe/${recipe.id}`, {
                              state: { 
                                fromIngredientSearch: true,
                                searchResults: foundRecipes,
                                selectedIngredients: selectedIngredients
                              }
                            });
                          }} style={{cursor: 'pointer', flex: 1, paddingLeft: '2.5rem'}}>
                            {recipe.title}
                          </h3>
                        </div>
                        
                        <div onClick={() => {
                          navigate(`/recipe/${recipe.id}`, {
                            state: { 
                              fromIngredientSearch: true,
                              searchResults: foundRecipes,
                              selectedIngredients: selectedIngredients
                            }
                          });
                        }} style={{cursor: 'pointer'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem'}}>
                            {category && <span className="recipe-category">{category.name}</span>}
                            <span style={{ 
                              background: '#87A96B', 
                              color: 'white', 
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              ✓ {recipe.match_count} correspondances
                            </span>
                            {recipe.status && getStatusBadge(recipe.status) && (
                              <span 
                                className="status-badge"
                                style={{
                                  background: getStatusBadge(recipe.status).color,
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                {getStatusBadge(recipe.status).emoji} {getStatusBadge(recipe.status).label}
                              </span>
                            )}
                            {recipe.diet_type && getDietBadge(recipe.diet_type) && (
                              <span 
                                className="diet-badge"
                                style={{
                                  background: getDietBadge(recipe.diet_type).color,
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                {getDietBadge(recipe.diet_type).emoji} {getDietBadge(recipe.diet_type).label}
                              </span>
                            )}
                            {recipe.contains_allergens && recipe.contains_allergens.length > 0 && (
                              <span 
                                className="allergen-badge"
                                style={{
                                  background: '#DC3545',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                                title={`Contient: ${recipe.contains_allergens.join(', ')}`}
                              >
                                <WarningIcon size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                                Allergènes
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="recipe-card-meta" style={{display: 'flex', gap: '0.75rem'}} onClick={() => {
                          navigate(`/recipe/${recipe.id}`, {
                            state: { 
                              fromIngredientSearch: true,
                              searchResults: foundRecipes,
                              selectedIngredients: selectedIngredients
                            }
                          });
                        }}>
                          {recipe.prep_time && (
                            <div className="meta-item">
                              <ChefHat size={16} />
                              <span>{recipe.prep_time}</span>
                            </div>
                          )}
                          {recipe.cook_time && (
                            <div className="meta-item">
                              <Flame size={16} />
                              <span>{recipe.cook_time}</span>
                            </div>
                          )}
                          {recipe.servings && (
                            <div className="meta-item">
                              <Users size={16} />
                              <span>{recipe.servings}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )
              ) : (
                displayRecipes.map(recipe => {
              const category = categories.find(c => c.id === recipe.category_id);
              // Toujours utiliser les ingrédients québécois (tasses/cuillères) pour l'affichage
              const ingredients = recipe.ingredients_quebec || recipe.ingredients_metric || [];
              
              return (
                <div 
                  key={recipe.id} 
                  className="recipe-card"
                  data-testid={`recipe-card-${recipe.id}`}
                  style={{ position: 'relative' }}
                  onMouseDown={(e) => handleLongPressStart(e, recipe)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={(e) => handleLongPressStart(e, recipe)}
                  onTouchEnd={handleLongPressEnd}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    handleLongPressStart(e, recipe);
                  }}
                >

                  {selectedCategory ? (
                    // MODE COMPACT: Dans une catégorie spécifique
                    <div 
                      onClick={() => navigate(`/recipe/${recipe.id}`)} 
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        width: '100%',
                        minHeight: '2.5rem'
                      }}
                    >
                      <h3 style={{
                        flex: 1,
                        paddingLeft: '2rem',
                        margin: 0,
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#3A4A3E',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}>
                        {recipe.is_popular && (
                          <span style={{ marginRight: '4px' }} title="Recette populaire - téléchargée 500+ fois!">🌟</span>
                        )}
                        {recipe.title}
                      </h3>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        flexShrink: 0,
                        flexWrap: 'nowrap'
                      }}>
                        {recipe.prep_time && (
                          <span style={{
                            fontSize: '0.7rem',
                            color: '#5D6F61',
                            whiteSpace: 'nowrap'
                          }}>
                            ⏱️{recipe.prep_time}
                          </span>
                        )}
                        {ingredients && ingredients.length > 0 && (
                          <span style={{
                            fontSize: '0.7rem',
                            color: '#5D6F61',
                            whiteSpace: 'nowrap'
                          }}>
                            🥘{ingredients.length}
                          </span>
                        )}
                        
                        {recipe.status && getStatusBadge(recipe.status) && (
                          <span 
                            style={{
                              background: getStatusBadge(recipe.status).color,
                              color: 'white',
                              padding: '0.15rem 0.35rem',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: 1
                            }}
                          >
                            {getStatusBadge(recipe.status).emoji}
                          </span>
                        )}
                        {recipe.diet_type && getDietBadge(recipe.diet_type) && (
                          <span 
                            style={{
                              background: getDietBadge(recipe.diet_type).color,
                              color: 'white',
                              padding: '0.15rem 0.35rem',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: 1
                            }}
                          >
                            {getDietBadge(recipe.diet_type).emoji}
                          </span>
                        )}
                        {recipe.contains_allergens && recipe.contains_allergens.length > 0 && (
                          <span 
                            style={{
                              background: '#DC3545',
                              color: 'white',
                              padding: '0.15rem 0.35rem',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: 1
                            }}
                            title={`Contient: ${recipe.contains_allergens.join(', ')}`}
                          >
                            <WarningIcon size={18} />
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    // MODE LARGE: Page principale (toutes les recettes)
                    <>
                      {/* Photo de la recette */}
                      {recipe.recipe_image_url && (
                        <div 
                          onClick={() => navigate(`/recipe/${recipe.id}`)} 
                          style={{
                            width: '100%',
                            height: '140px',
                            borderRadius: '12px 12px 0 0',
                            overflow: 'hidden',
                            marginBottom: '0.5rem',
                            cursor: 'pointer'
                          }}
                        >
                          <img 
                            src={recipe.recipe_image_url.startsWith('http') ? recipe.recipe_image_url : `${API}${recipe.recipe_image_url}`}
                            alt={recipe.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      
                      <div className="recipe-card-header">
                        <h3 onClick={() => navigate(`/recipe/${recipe.id}`)} style={{cursor: 'pointer', flex: 1, paddingLeft: recipe.recipe_image_url ? '0.5rem' : '2.5rem'}}>
                          {recipe.title}
                        </h3>
                      </div>
                      
                      {/* Créateur */}
                      {recipe.creator_name && (
                        <p style={{
                          fontSize: '0.8rem',
                          color: '#8B7355',
                          margin: '0 0 0.5rem 0.5rem',
                          fontStyle: 'italic'
                        }}>
                          Par {recipe.creator_name}
                        </p>
                      )}
                      
                      <div onClick={() => navigate(`/recipe/${recipe.id}`)} style={{cursor: 'pointer'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem'}}>
                          {category && <span className="recipe-category">{category.name}</span>}
                          {recipe.status && getStatusBadge(recipe.status) && (
                            <span 
                              className="status-badge"
                              style={{
                                background: getStatusBadge(recipe.status).color,
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              {getStatusBadge(recipe.status).emoji} {getStatusBadge(recipe.status).label}
                            </span>
                          )}
                          {recipe.diet_type && getDietBadge(recipe.diet_type) && (
                            <span 
                              className="diet-badge"
                              style={{
                                background: getDietBadge(recipe.diet_type).color,
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              {getDietBadge(recipe.diet_type).emoji} {getDietBadge(recipe.diet_type).label}
                            </span>
                          )}
                          {recipe.contains_allergens && recipe.contains_allergens.length > 0 && (
                            <span 
                              className="allergen-badge"
                              style={{
                                background: '#DC3545',
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              title={`Contient: ${recipe.contains_allergens.join(', ')}`}
                            >
                              <WarningIcon size={18} /> Allergènes
                            </span>
                          )}
                        </div>
                        <div className="recipe-card-meta">
                          {recipe.prep_time && (
                            <div className="meta-item">
                              <ChefHat size={16} />
                              <span>{recipe.prep_time}</span>
                            </div>
                          )}
                          {recipe.cook_time && (
                            <div className="meta-item">
                              <Flame size={16} />
                              <span>{recipe.cook_time}</span>
                            </div>
                          )}
                          {recipe.servings && (
                            <div className="meta-item">
                              <Users size={16} />
                              <span>{recipe.servings}</span>
                            </div>
                          )}
                        </div>
                        <p className="recipe-ingredients">
                          {ingredients.slice(0, 3).join(", ")}
                          {ingredients.length > 3 && "..."}
                        </p>
                      </div>
                      
                      {/* Lien vers la vidéo source */}
                      {recipe.video_source_url && (
                        <a 
                          href={recipe.video_source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.75rem',
                            color: '#D4A574',
                            textDecoration: 'none',
                            padding: '0.3rem 0.5rem',
                            marginLeft: '0.5rem',
                            marginBottom: '0.5rem',
                            border: '1px solid #D4A574',
                            borderRadius: '6px'
                          }}
                        >
                          <ExternalLink size={12} />
                          Voir la vidéo
                        </a>
                      )}
                    </>
                  )}
                </div>
              );
            })
          );
        })()}
      </div>
      </>
      )}
        
      {/* Floating Add Button - caché quand un dialog est ouvert */}
        {!showAddDialog && !showCategoryDialog && !showCompatibilityDialog && !showMoveDialog && !showDeleteDialog && !showFiltersDialog && !showIngredientSearch && (
          <button 
            className="floating-add-button"
            onClick={() => setShowAddDialog(true)}
            aria-label="Ajouter une recette"
            data-testid="floating-add-btn"
            disabled={categories.length === 0}
          >
            <Plus size={32} />
          </button>
        )}
      </div>

      {/* Filters Dialog */}
      <Dialog open={showFiltersDialog} onOpenChange={setShowFiltersDialog}>
        <DialogContent data-testid="filters-dialog">
          <DialogHeader>
            <DialogTitle>Rechercher et filtrer</DialogTitle>
          </DialogHeader>
          <div className="filters-form">
            <div className="filter-section">
              <Label>Temps de préparation</Label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${prepTimeFilter === 15 ? 'active' : ''}`}
                  onClick={() => setPrepTimeFilter(prepTimeFilter === 15 ? null : 15)}
                  data-testid="filter-prep-15"
                >
                  Moins de 15 min
                </button>
                <button
                  className={`filter-btn ${prepTimeFilter === 30 ? 'active' : ''}`}
                  onClick={() => setPrepTimeFilter(prepTimeFilter === 30 ? null : 30)}
                  data-testid="filter-prep-30"
                >
                  Moins de 30 min
                </button>
                <button
                  className={`filter-btn ${prepTimeFilter === 60 ? 'active' : ''}`}
                  onClick={() => setPrepTimeFilter(prepTimeFilter === 60 ? null : 60)}
                  data-testid="filter-prep-60"
                >
                  Moins d'1 heure
                </button>
              </div>
            </div>

            <div className="filter-section">
              <Label>Nombre d'ingrédients</Label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${ingredientFilter === '5-7' ? 'active' : ''}`}
                  onClick={() => setIngredientFilter(ingredientFilter === '5-7' ? null : '5-7')}
                  data-testid="filter-ingredients-5-7"
                >
                  5 à 7
                </button>
                <button
                  className={`filter-btn ${ingredientFilter === '8-10' ? 'active' : ''}`}
                  onClick={() => setIngredientFilter(ingredientFilter === '8-10' ? null : '8-10')}
                  data-testid="filter-ingredients-8-10"
                >
                  8 à 10
                </button>
                <button
                  className={`filter-btn ${ingredientFilter === '10+' ? 'active' : ''}`}
                  onClick={() => setIngredientFilter(ingredientFilter === '10+' ? null : '10+')}
                  data-testid="filter-ingredients-10+"
                >
                  10 et plus
                </button>
              </div>
            </div>

            <div className="filter-section">
              <Label>Type de viande</Label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${meatTypeFilter === 'poulet' ? 'active' : ''}`}
                  onClick={() => setMeatTypeFilter(meatTypeFilter === 'poulet' ? null : 'poulet')}
                  data-testid="filter-meat-poulet"
                >
                  Poulet
                </button>
                <button
                  className={`filter-btn ${meatTypeFilter === 'boeuf' ? 'active' : ''}`}
                  onClick={() => setMeatTypeFilter(meatTypeFilter === 'boeuf' ? null : 'boeuf')}
                  data-testid="filter-meat-boeuf"
                >
                  Bœuf
                </button>
                <button
                  className={`filter-btn ${meatTypeFilter === 'porc' ? 'active' : ''}`}
                  onClick={() => setMeatTypeFilter(meatTypeFilter === 'porc' ? null : 'porc')}
                  data-testid="filter-meat-porc"
                >
                  Porc
                </button>
                <button
                  className={`filter-btn ${meatTypeFilter === 'poisson' ? 'active' : ''}`}
                  onClick={() => setMeatTypeFilter(meatTypeFilter === 'poisson' ? null : 'poisson')}
                  data-testid="filter-meat-poisson"
                >
                  Poisson
                </button>
                <button
                  className={`filter-btn ${meatTypeFilter === 'vegetarien' ? 'active' : ''}`}
                  onClick={() => setMeatTypeFilter(meatTypeFilter === 'vegetarien' ? null : 'vegetarien')}
                  data-testid="filter-meat-vegetarien"
                >
                  Végétarien
                </button>
              </div>
            </div>

            <div className="filter-section">
              <Label>Statut de la recette</Label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${statusFilter === 'à_faire' ? 'active' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === 'à_faire' ? null : 'à_faire')}
                >
                  🔵 À faire
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'fait' ? 'active' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === 'fait' ? null : 'fait')}
                >
                  <CheckIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                  Fait
                </button>
                <button
                  className={`filter-btn ${statusFilter === 'à_refaire' ? 'active' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === 'à_refaire' ? null : 'à_refaire')}
                >
                  <StarIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                  À refaire
                </button>
              </div>
            </div>

            <div className="filter-actions">
              <Button
                variant="outline"
                onClick={() => {
                  setPrepTimeFilter(null);
                  setIngredientFilter(null);
                  setMeatTypeFilter(null);
                  setStatusFilter(null);
                }}
                data-testid="clear-filters-btn"
              >
                Effacer les filtres
              </Button>
              <Button
                onClick={() => setShowFiltersDialog(false)}
                data-testid="apply-filters-btn"
              >
                Appliquer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Recipe Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Déplacer la recette</DialogTitle>
          </DialogHeader>
          <div className="form-group">
            <Label>Nouvelle catégorie</Label>
            <Select value={newCategoryForMove} onValueChange={setNewCategoryForMove}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="dialog-actions" style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem'}}>
            <Button
              variant="outline"
              onClick={() => {
                setShowMoveDialog(false);
                setRecipeToMove(null);
                setNewCategoryForMove("");
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleMoveRecipe}>
              Déplacer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Recipe Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la recette</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '1rem 0' }}>
            <p style={{ marginBottom: '1.5rem', color: '#5D6F61' }}>
              Êtes-vous sûr de vouloir supprimer la recette <strong>"{recipeToDelete?.title}"</strong> ?
              Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button 
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
              >
                Annuler
              </Button>
              <Button 
                onClick={handleDeleteRecipe}
                style={{ 
                  background: '#DC3545',
                  color: 'white'
                }}
              >
                <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ingredient Search Dialog - "Qu'est-ce qu'on mange?" */}
      <Dialog open={showIngredientSearch} onOpenChange={setShowIngredientSearch}>
        <DialogContent className="ingredient-search-dialog" style={{ maxWidth: '900px', maxHeight: '80vh' }}>
          <DialogHeader>
            <DialogTitle>
              <MealIcon size={22} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
              Qu'est-ce qu'on mange?
            </DialogTitle>
            <p style={{ fontSize: '0.9rem', color: '#8B7355', marginTop: '0.5rem' }}>
              Cochez les ingrédients que vous avez sous la main (minimum 5 pour trouver des recettes)
            </p>
          </DialogHeader>
          
          <div style={{ padding: '1rem 0', maxHeight: '60vh', overflowY: 'auto' }}>
            {Object.entries(AVAILABLE_INGREDIENTS).map(([category, ingredients]) => (
              <div key={category} style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#3A4A3E',
                  marginBottom: '0.75rem',
                  borderBottom: '2px solid #E8E4D9',
                  paddingBottom: '0.5rem'
                }}>
                  {category}
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '0.5rem'
                }}>
                  {ingredients.map(ingredient => (
                    <label
                      key={ingredient}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem',
                        background: selectedIngredients.includes(ingredient) ? '#E8F4DB' : '#F5F1E8',
                        border: selectedIngredients.includes(ingredient) ? '2px solid #87A96B' : '2px solid transparent',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontSize: '0.85rem'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedIngredients.includes(ingredient)) {
                          e.currentTarget.style.background = '#E8E4D9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedIngredients.includes(ingredient)) {
                          e.currentTarget.style.background = '#F5F1E8';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIngredients.includes(ingredient)}
                        onChange={() => toggleIngredient(ingredient)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{ingredient}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ 
            borderTop: '1px solid #E8E4D9', 
            paddingTop: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <p style={{ fontSize: '0.9rem', color: '#5D6F61' }}>
              <strong>{selectedIngredients.length}</strong> ingrédient(s) sélectionné(s)
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedIngredients([]);
                  setFoundRecipes([]);
                }}
              >
                Réinitialiser
              </Button>
              <Button
                onClick={handleSearchByIngredients}
                disabled={searchingRecipes || selectedIngredients.length === 0}
                style={{ background: '#87A96B', color: 'white' }}
              >
                {searchingRecipes ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Recherche...
                  </>
                ) : (
                  'Trouver une recette'
                )}
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Inspire-moi Dialog */}
      <Dialog open={showInspireDialog} onOpenChange={setShowInspireDialog}>
        <DialogContent style={{ maxWidth: '400px' }}>
          <DialogHeader>
            <DialogTitle>
              <CelebrationIcon size={22} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
              Inspire-moi!
            </DialogTitle>
          </DialogHeader>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1rem',
            padding: '1rem 0'
          }}>
            <p style={{ color: '#5D6F61', marginBottom: '0.5rem' }}>
              Choisis une catégorie et découvre une recette au hasard:
            </p>
            <Button
              onClick={() => {
                setShowInspireDialog(false);
                handleGetRandomRecipe('à_faire');
              }}
              style={{
                background: '#2196F3',
                color: 'white',
                padding: '1rem',
                fontSize: '1rem'
              }}
            >
              <TimeIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
              À faire
            </Button>
            <Button
              onClick={() => {
                setShowInspireDialog(false);
                handleGetRandomRecipe('fait');
              }}
              style={{
                background: '#4CAF50',
                color: 'white',
                padding: '1rem',
                fontSize: '1rem'
              }}
            >
              <CheckIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
              Fait
            </Button>
            <Button
              onClick={() => {
                setShowInspireDialog(false);
                handleGetRandomRecipe('à_refaire');
              }}
              style={{
                background: '#FFC107',
                color: 'white',
                padding: '1rem',
                fontSize: '1rem'
              }}
            >
              <StarIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
              À refaire
            </Button>
            <Button
              onClick={() => {
                setShowInspireDialog(false);
                handleGetRandomRecipe('tout');
              }}
              style={{
                background: '#87A96B',
                color: 'white',
                padding: '1rem',
                fontSize: '1rem'
              }}
            >
              <MealIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
              Tout
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent data-testid="category-dialog" style={{ maxHeight: '85vh', paddingBottom: '100px' }}>
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
          </DialogHeader>
          <div className="category-form" style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 180px)' }}>
            <div className="form-group">
              <Label>Nom de la catégorie</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ex: Desserts, Plats principaux..."
                data-testid="category-name-input"
              />
            </div>
            <div className="form-group">
              <Label>Image de la catégorie</Label>
              
              {/* Option d'upload personnalisé */}
              <div style={{marginBottom: '1rem'}}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{display: 'none'}}
                  id="category-image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('category-image-upload').click()}
                  style={{width: '100%'}}
                >
                  <CameraIcon size={18} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Télécharger ma propre photo
                </Button>
                {customImagePreview && (
                  <div style={{marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <img src={customImagePreview} alt="Aperçu" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px'}} />
                    <span style={{fontSize: '0.85rem', color: '#8B7355'}}>Image personnalisée</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomImageFile(null);
                        setCustomImagePreview(null);
                        setNewCategoryImage(DEFAULT_CATEGORY_IMAGES[0]);
                      }}
                    >
                      ✕
                    </Button>
                  </div>
                )}
              </div>
              
              <Label style={{fontSize: '0.9rem', color: '#8B7355', marginBottom: '0.5rem'}}>Ou choisir parmi les images suggérées:</Label>
              <div className="image-selector">
                {DEFAULT_CATEGORY_IMAGES.map((img, idx) => (
                  <div 
                    key={`cat-img-${img.slice(-10)}`}
                    className={`image-option ${newCategoryImage === img ? 'selected' : ''}`}
                    onClick={() => setNewCategoryImage(img)}
                    data-testid={`category-image-${idx}`}
                  >
                    <img src={img} alt={`Option ${idx + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Bouton Créer fixé en bas du dialogue */}
          <div style={{ 
            position: 'sticky', 
            bottom: 0, 
            background: 'white', 
            padding: '1rem 0',
            marginTop: '1rem',
            borderTop: '1px solid #E8E4D9'
          }}>
            <Button 
              onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
              className="w-full"
              data-testid="save-category-btn"
              style={{ 
                background: '#87A96B', 
                color: 'white', 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              {editingCategory ? "Modifier la catégorie" : "Créer la catégorie"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Recipe Dialog */}
      <AddRecipeDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        extractionMode={extractionMode}
        setExtractionMode={setExtractionMode}
        videoUrl={videoUrl}
        setVideoUrl={setVideoUrl}
        extracting={extracting}
        extractionStep={extractionStep}
        extractionProgress={extractionProgress}
        isPreparingExtraction={isPreparingExtraction}
        recipeImageFiles={recipeImageFiles}
        recipeImagePreviews={recipeImagePreviews}
        recipeImageInput={recipeImageInput}
        recipeSource={recipeSource}
        setRecipeSource={setRecipeSource}
        categories={categories}
        selectedCategoryForRecipe={selectedCategoryForRecipe}
        setSelectedCategoryForRecipe={setSelectedCategoryForRecipe}
        showInlineNewCategory={showInlineNewCategory}
        setShowInlineNewCategory={setShowInlineNewCategory}
        inlineNewCategoryName={inlineNewCategoryName}
        setInlineNewCategoryName={setInlineNewCategoryName}
        creatingInlineCategory={creatingInlineCategory}
        manualRecipe={manualRecipe}
        setManualRecipe={setManualRecipe}
        savingManualRecipe={savingManualRecipe}
        checkVideoDurationAndExtract={checkVideoDurationAndExtract}
        handleExtractFromImage={handleExtractFromImage}
        handleImageUploadForRecipe={handleImageUploadForRecipe}
        handleCreateInlineCategory={handleCreateInlineCategory}
        handleSaveManualRecipe={handleSaveManualRecipe}
        addIngredientField={addIngredientField}
        removeIngredientField={removeIngredientField}
        updateIngredient={updateIngredient}
        addInstructionField={addInstructionField}
        removeInstructionField={removeInstructionField}
        updateInstruction={updateInstruction}
        removeImage={removeImage}
      />


      {/* Dialog de confirmation pour vidéos longues (3:30-5:00) */}
      <Dialog open={showDurationConfirmDialog} onOpenChange={setShowDurationConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.5rem', color: '#5D6F61', textAlign: 'center' }}>
              ⏱️ Vidéo longue détectée
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ padding: '1rem' }}>
            <div style={{
              background: '#FFF9E6',
              border: '2px solid #FFC107',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏰</div>
              <p style={{ fontSize: '1.1rem', color: '#5D6F61', marginBottom: '0.5rem' }}>
                Cette vidéo dure <strong>{videoDuration?.toFixed(2)} minutes</strong>
              </p>
              <p style={{ fontSize: '0.95rem', color: '#8B7355' }}>
                Les vidéos de plus de 3:30 minutes coûtent <strong>2 crédits</strong> au lieu de 1.
              </p>
            </div>

            <div style={{
              background: '#F5F1E8',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <ChefIcon size={20} color="#87A96B" />
                <span style={{ fontWeight: '600', color: '#5D6F61' }}>Coût de cette extraction :</span>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#87A96B', textAlign: 'center' }}>
                2 crédits
              </div>
              <div style={{ fontSize: '0.85rem', color: '#8B7355', textAlign: 'center', marginTop: '0.25rem' }}>
                Téléchargements restants : {isUnlimited ? '∞' : downloadsRemaining}
              </div>
            </div>

            <p style={{ fontSize: '0.9rem', color: '#5D6F61', textAlign: 'center', marginBottom: '1.5rem' }}>
              Voulez-vous continuer avec cette extraction ?
            </p>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button
                onClick={() => setShowDurationConfirmDialog(false)}
                variant="outline"
                style={{ flex: 1 }}
              >
                Annuler
              </Button>
              <Button
                onClick={() => handleExtractRecipe(2)}
                style={{
                  flex: 1,
                  background: '#87A96B',
                  color: 'white'
                }}
              >
                Confirmer (2 crédits)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compatibility Dialog */}
      <Dialog open={showCompatibilityDialog} onOpenChange={setShowCompatibilityDialog}>
        <DialogContent className="compatibility-dialog" data-testid="compatibility-dialog">
          <DialogHeader>
            <DialogTitle>⚠️ Incompatibilités détectées</DialogTitle>
          </DialogHeader>
          <div className="compatibility-content">
            <p className="compatibility-message">
              L'IA a détecté des ingrédients incompatibles avec vos préférences alimentaires.
              Voici les alternatives suggérées:
            </p>
            
            {compatibilityData?.issues && compatibilityData.issues.length > 0 && (
              <div className="compatibility-issues">
                <h4>🚨 Problèmes identifiés:</h4>
                <ul>
                  {compatibilityData.issues.map((issue, idx) => (
                    <li key={`issue-${issue.ingredient}-${idx}`} className={`issue-item ${issue.type}`}>
                      <strong>{issue.ingredient}</strong>
                      <span className="issue-reason">
                        {issue.type === 'allergy' ? '⚠️ Allergie' : '🍽️ Restriction'}: {issue.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {compatibilityData?.alternatives && Object.keys(compatibilityData.alternatives).length > 0 && (
              <div className="compatibility-alternatives">
                <h4>✅ Alternatives suggérées (choisissez votre préférée):</h4>
                <div className="alternatives-list">
                  {Object.entries(compatibilityData.alternatives).map(([original, alternatives], idx) => (
                    <div key={`alt-${original}`} className="alternative-group">
                      <div className="alternative-original">
                        <strong>Remplacer:</strong> {original}
                      </div>
                      <div className="alternative-options">
                        {Array.isArray(alternatives) ? (
                          alternatives.map((alt, altIdx) => (
                            <div 
                              key={`alt-opt-${alt.name || altIdx}`} 
                              className={`alternative-option ${(selectedAlternatives[original] === altIdx || (!selectedAlternatives[original] && altIdx === 0)) ? 'selected' : ''}`}
                              onClick={() => setSelectedAlternatives({...selectedAlternatives, [original]: altIdx})}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="option-header">
                                <span className="option-number">Option {altIdx + 1}:</span>
                                <strong>{alt.name}</strong>
                                {(selectedAlternatives[original] === altIdx || (!selectedAlternatives[original] && altIdx === 0)) && (
                                  <span style={{ marginLeft: 'auto', color: '#87A96B', fontSize: '1.2rem' }}>✓</span>
                                )}
                              </div>
                              <div className="alternative-measures">
                                <span className="measure-quebec">{alt.quebec_measure}</span>
                                <span className="measure-metric">({alt.metric_measure})</span>
                              </div>
                              {alt.note && <div className="alternative-note">💡 {alt.note}</div>}
                            </div>
                          ))
                        ) : (
                          // Ancien format de compatibilité (single alternative)
                          <div className="alternative-option selected">
                            <div className="option-header">
                              <strong>{alternatives.suggestion || alternatives.name}</strong>
                            </div>
                            <div className="alternative-measures">
                              <span className="measure-quebec">{alternatives.quebec_measure}</span>
                              <span className="measure-metric">({alternatives.metric_measure})</span>
                            </div>
                            {alternatives.note && <div className="alternative-note">💡 {alternatives.note}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="compatibility-actions">
              <Button
                variant="outline"
                onClick={handleKeepOriginal}
                className="keep-original-btn"
              >
                Garder la recette originale
              </Button>
              <Button
                onClick={handleAcceptAlternatives}
                className="accept-alternatives-btn"
              >
                ✓ Accepter les modifications
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Abonnement */}
      <SubscriptionDialog
        open={showSubscriptionDialog}
        onOpenChange={setShowSubscriptionDialog}
        subscriptionPlan={subscriptionPlan}
        subscriptionPlanName={subscriptionPlanName}
        isUnlimited={isUnlimited}
        downloadsRemaining={downloadsRemaining}
        downloadsLimit={downloadsLimit}
        extraDownloads={extraDownloads}
        daysUntilRenewal={daysUntilRenewal}
        pendingPlanChange={pendingPlanChange}
        subscriptionPlans={subscriptionPlans}
        loadingPlans={loadingPlans}
        loadSubscriptionPlans={loadSubscriptionPlans}
        loadSubscriptionStatus={loadSubscriptionStatus}
      />
      
      {/* Menu Long Press pour Catégories */}
      <Dialog open={categoryLongPressMenu.show} onOpenChange={closeCategoryLongPressMenu}>
        <DialogContent style={{
          maxWidth: '320px',
          padding: '1rem'
        }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.1rem', color: '#3A4A3E' }}>
              Options pour "{categoryLongPressMenu.category?.name}"
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {/* Modifier */}
            <button
              onClick={() => {
                openCategoryDialog(categoryLongPressMenu.category);
                closeCategoryLongPressMenu();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1rem',
                background: '#F5F1E8',
                border: '1px solid #D4A574',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.95rem',
                color: '#3A4A3E',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E8E4D9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F5F1E8'}
            >
              <Edit3 size={18} style={{ color: '#87A96B' }} />
              Modifier
            </button>

            {/* Supprimer */}
            <button
              onClick={() => {
                confirmDeleteCategory(categoryLongPressMenu.category);
                closeCategoryLongPressMenu();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1rem',
                background: '#FFE8E8',
                border: '1px solid #DC3545',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.95rem',
                color: '#DC3545',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#FFD0D0'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#FFE8E8'}
            >
              <Trash2 size={18} />
              Supprimer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression de catégorie */}
      <Dialog open={showDeleteCategoryDialog} onOpenChange={setShowDeleteCategoryDialog}>
        <DialogContent style={{
          maxWidth: '400px',
          padding: '1.5rem'
        }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.3rem', color: '#DC3545', marginBottom: '1rem' }}>
              ⚠️ Attention !
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.95rem', color: '#3A4A3E', marginBottom: '1rem', lineHeight: '1.6' }}>
              Vous êtes sur le point de supprimer la catégorie <strong>"{categoryToDelete?.name}"</strong>.
            </p>
            <div style={{
              background: '#FFF3CD',
              border: '1px solid #FFC107',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#856404', fontWeight: '600', marginBottom: '0.5rem' }}>
                🗑️ Cette action est DÉFINITIVE !
              </p>
              <p style={{ fontSize: '0.85rem', color: '#856404', lineHeight: '1.5' }}>
                Toutes les recettes de cette catégorie seront également supprimées de façon permanente.
              </p>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#6C757D', fontStyle: 'italic' }}>
              Êtes-vous absolument certain(e) de vouloir continuer ?
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => {
                setShowDeleteCategoryDialog(false);
                setCategoryToDelete(null);
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#F5F1E8',
                border: '1px solid #D4A574',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                color: '#3A4A3E',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E8E4D9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F5F1E8'}
            >
              Annuler
            </button>
            <button
              onClick={handleDeleteCategory}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#DC3545',
                border: '1px solid #DC3545',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                color: 'white',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#C82333'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#DC3545'}
            >
              Oui, supprimer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Long Press pour Recettes */}
      <Dialog open={longPressMenu.show} onOpenChange={closeLongPressMenu}>
        <DialogContent style={{
          maxWidth: '320px',
          padding: '1rem'
        }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.1rem', color: '#3A4A3E' }}>
              Options pour "{longPressMenu.recipe?.title}"
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {/* Modifier */}
            <button
              onClick={() => {
                navigate(`/recipe/${longPressMenu.recipe?.id}`);
                closeLongPressMenu();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1rem',
                background: '#F5F1E8',
                border: '1px solid #D4A574',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.95rem',
                color: '#3A4A3E',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E8E4D9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F5F1E8'}
            >
              <Edit3 size={18} style={{ color: '#87A96B' }} />
              Modifier
            </button>

            {/* Déplacer */}
            <button
              onClick={() => {
                setRecipeToMove(longPressMenu.recipe);
                setNewCategoryForMove(longPressMenu.recipe?.category_id);
                setShowMoveDialog(true);
                closeLongPressMenu();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1rem',
                background: '#F5F1E8',
                border: '1px solid #D4A574',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.95rem',
                color: '#3A4A3E',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E8E4D9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F5F1E8'}
            >
              <MoveHorizontal size={18} style={{ color: '#87A96B' }} />
              Déplacer
            </button>

            {/* Supprimer */}
            <button
              onClick={() => {
                setRecipeToDelete(longPressMenu.recipe);
                setShowDeleteDialog(true);
                closeLongPressMenu();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1rem',
                background: '#FFE8E8',
                border: '1px solid #DC3545',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.95rem',
                color: '#DC3545',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#FFD0D0'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#FFE8E8'}
            >
              <Trash2 size={18} />
              Supprimer
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bannière publicitaire fixe en bas */}
      <AdBanner />
      
      <Footer />
    </div>
  );
}
